'use client';

import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import Image from 'next/image';

export interface FAQItem {
    id: number;
    question: string;
    answer: string;
}

const faqData: FAQItem[] = [
    {
        id: 1,
        question: "Where Can I Find Information?",
        answer: "You can find comprehensive information about our luxury villas, amenities, and services on our website. Our detailed property listings include high-quality photos, virtual tours, and complete descriptions of all features and services available."
    },
    {
        id: 2,
        question: "What Are Your Terms And Conditions?",
        answer: "Our terms and conditions cover booking policies, cancellation fees, check-in/check-out times, and house rules. We provide full transparency on all policies to ensure a smooth and enjoyable stay for our guests."
    },
    {
        id: 3,
        question: "Can I Book Directly From The Website?",
        answer: "Yes, you can book directly through our secure online booking system. We also offer personalized booking assistance through our concierge team for special requests or group bookings."
    },
    {
        id: 4,
        question: "What Kinds of Payment Do You Accept?",
        answer: "We accept all major credit cards (Visa, MasterCard, American Express), bank transfers, and secure online payment gateways. We also offer flexible payment plans for longer stays."
    },
    {
        id: 5,
        question: "When do I receive my booking confirmation?",
        answer: "You'll receive instant booking confirmation via email immediately after completing your reservation. Our team will also send detailed arrival instructions and villa access information 24 hours before your check-in date."
    }
];

export default function FAQ({ faqData }: { faqData: FAQItem[] }) {
    const [openItem, setOpenItem] = useState<number>(1);

    const toggleItem = (id: number) => {
        setOpenItem(openItem === id ? 0 : id);
    };

    return (
        <section className="py-20 bg-white">
            <div className="container mx-auto px-4">
                <div className=" mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        {/* Left Section - FAQ */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            {/* Title */}
                            <div className="space-y-4">
                                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                                    Frequently Asked Questions
                                </h2>
                            </div>

                            {/* FAQ Accordion */}
                            <div className="space-y-4">
                                {faqData.map((item) => (
                                    <motion.div
                                        key={item.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: item.id * 0.1 }}
                                        viewport={{ once: true }}
                                        className="border-b border-gray-200 pb-4"
                                    >
                                        {/* Question */}
                                        <button
                                            onClick={() => toggleItem(item.id)}
                                            className="w-full flex items-center justify-between text-left py-4 hover:text-gray-700 transition-colors duration-200"
                                        >
                                            <span className="text-lg font-semibold text-gray-900 pr-4">
                                                {item.id}. {item.question}
                                            </span>
                                            {openItem === item.id ? (
                                                <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
                                            )}
                                        </button>

                                        {/* Answer */}
                                        {openItem === item.id && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: "auto" }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="overflow-hidden"
                                            >
                                                <p className="text-gray-600 leading-relaxed pr-8">
                                                    {item.answer}
                                                </p>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Right Section - Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl"
                        >
                            <Image
                                src="https://images.pexels.com/photos/5428830/pexels-photo-5428830.jpeg"
                                alt="Luxury Villa Interior"
                                fill
                                className="object-cover"
                                // sizes="(max-width: 768px) 100vw, 50vw"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
